import { PREFS_KEY, API_ORIGIN, WIDGET_CDN_ORIGIN, WIDGET_VERSION, type UserPrefs } from '@nagisot/shared';
import { applyClientPrefs, setFeatureOverrides } from './prefs/apply.js';
import { startSelectionTracking } from './effects/tts.js';
import { initAnalytics, track } from './analytics.js';
import { startSeizureGuard } from './runtime/seizure-guard.js';
import { startLiveBaseline, ensureSkipLink, ensureMainLandmark } from './runtime/live-baseline.js';
import { contentHashCandidates } from '@nagisot/shared/element-id';
import { applyContrastFix } from './runtime/contrast-fix.js';
import { normalizeHeadingOutline, ensureLandmark, makeClickableAccessible, addMediaPause } from './runtime/structure-fixes.js';

const SCRIPT_TAG_NAME = 'nagisot-loader';
const HOST_ID = 'nagisot-host';

// NOTE: ICON_SVG and FAB_CSS must be declared BEFORE bootstrap() runs.
// With `defer` the script executes when document.readyState is 'interactive',
// which means mountFab() is called synchronously and references these
// constants. If they're declared after the IIFE, they're in the temporal
// dead zone and cause "Cannot access before initialization".

// Refined "human form" with a touch of editorial elegance — symmetrical, balanced.
const ICON_SVG = `
<svg viewBox="0 0 26 26" width="22" height="22" aria-hidden="true" focusable="false">
  <circle cx="13" cy="5" r="2.2" fill="currentColor"/>
  <path d="M5 9.5h16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
  <path d="M13 9.5v5l-3.2 8.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" fill="none"/>
  <path d="M13 14.5l3.2 8.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" fill="none"/>
</svg>`;

const FAB_CSS = `
:host { all: initial; }
@keyframes nag-fab-in {
  from { opacity: 0; transform: translateY(12px) scale(.92); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes nag-pulse {
  0%, 100% { box-shadow: 0 8px 24px rgba(11,18,32,.22), 0 0 0 0 rgba(184,149,106,0); }
  50%      { box-shadow: 0 10px 28px rgba(11,18,32,.28), 0 0 0 10px rgba(184,149,106,0); }
}
.fab {
  position: fixed;
  inset-block-end: 22px;
  inset-inline-end: 22px;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  border: 1px solid rgba(184,149,106,0.45);
  background: radial-gradient(circle at 30% 30%, #1a2433 0%, #0B1220 60%, #060a13 100%);
  color: #D7B98C;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow:
    0 8px 24px rgba(11,18,32,.22),
    0 0 0 0 rgba(184,149,106,0),
    inset 0 1px 0 rgba(255,255,255,.06);
  z-index: 2147483646;
  transition:
    transform .28s cubic-bezier(.2,.8,.2,1),
    box-shadow .28s cubic-bezier(.2,.8,.2,1),
    border-color .2s ease;
  animation: nag-fab-in .55s cubic-bezier(.16,1,.3,1) both;
  -webkit-tap-highlight-color: transparent;
  touch-action: none; /* let pointer-drag work on touch without scrolling */
}
/* While being dragged: snappy follow (no position easing) + grabbing cursor. */
.fab.nag-dragging {
  cursor: grabbing;
  transition: box-shadow .2s ease, border-color .2s ease;
  box-shadow: 0 18px 40px rgba(11,18,32,.38), 0 0 0 6px rgba(184,149,106,.20);
}
.fab.nag-dragging svg { transform: none; }
.fab svg { transition: transform .35s cubic-bezier(.16,1,.3,1); }
.fab:hover {
  transform: translateY(-2px);
  border-color: rgba(184,149,106,0.75);
  box-shadow:
    0 14px 32px rgba(11,18,32,.32),
    0 0 0 6px rgba(184,149,106,.16),
    inset 0 1px 0 rgba(255,255,255,.08);
}
.fab:hover svg { transform: rotate(-6deg) scale(1.06); }
.fab:active { transform: translateY(0) scale(.97); }
.fab:focus-visible {
  outline: none;
  box-shadow:
    0 8px 24px rgba(11,18,32,.22),
    0 0 0 4px rgba(184,149,106,.45);
  border-color: rgba(184,149,106,1);
}
.fab[aria-busy="true"] svg { opacity: 0.5; }
.fab[aria-busy="true"]::after {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: 50%;
  border: 1.5px solid transparent;
  border-top-color: #D7B98C;
  animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
  .fab, .fab svg, .fab::after { transition: none !important; animation: none !important; }
}
@media (prefers-color-scheme: light) {
  /* Same dark FAB in light mode — looks luxurious on white pages */
}
`;

interface LoaderOptions {
  siteId: string;
  apiOrigin: string;
  widgetUrl: string;
  /** Corner set by the embedding platform via data-position (e.g. PARSI's
   *  per-store setting). Overrides the dashboard default for this embed. */
  position?: 'br' | 'bl' | 'tr' | 'tl';
}

/** Accept our short codes and the long form host platforms send ("bottom-left"). */
function parsePosition(v: string | undefined): LoaderOptions['position'] {
  const s = (v || '').trim().toLowerCase();
  if (s === 'br' || s === 'bl' || s === 'tr' || s === 'tl') return s;
  const m = /^(top|bottom)[-_ ]?(left|right)$/.exec(s);
  return m ? (`${m[1]![0]}${m[2]![0]}` as LoaderOptions['position']) : undefined;
}

// Page-memory cache for stored fixes (see readFixCache). Declared before
// bootstrap() because it reads them synchronously.
const FIX_CACHE_PREFIX = 'nagisot:fx:';
const FIX_CACHE_INDEX = 'nagisot:fx-index';
const FIX_CACHE_TTL_MS = 7 * 24 * 3600 * 1000;
const FIX_CACHE_MAX_PAGES = 40;

(function bootstrap() {
  const script = document.currentScript as HTMLScriptElement | null;
  const siteId = script?.dataset.site;
  if (!siteId) {
    console.warn('[nagisot] missing data-site attribute');
    return;
  }
  // Defaults come from @nagisot/shared; the script tag can override per-customer.
  const apiOrigin = script?.dataset.api || API_ORIGIN;
  // Version the widget.js import too. Without a version query, a host site's
  // Service Worker (cache-first runtime caching) pins the very first widget.js
  // it ever saw and serves it forever — so widget updates never reach visitors.
  // A versioned URL is a distinct cache key, so each release is fetched fresh.
  const widgetUrl = script?.dataset.widget || `${WIDGET_CDN_ORIGIN}/widget.js?${WIDGET_VERSION}`;

  const opts: LoaderOptions = { siteId, apiOrigin, widgetUrl, position: parsePosition(script?.dataset.position) };

  // 0. If the page's CSP lets the loader run but blocks our API/CDN/icon, say
  //    exactly what to add — otherwise this fails as silently as a full block.
  watchCspViolations([apiOrigin, WIDGET_CDN_ORIGIN]);

  // 1. Pre-apply saved prefs immediately to avoid FOUC
  applySavedPrefs();

  // 1a. Seizure guard (WCAG 2.3.1) — freeze >3Hz flashing on every page,
  //     scanned or not, before the visitor interacts. No API, no AI.
  startSeizureGuard();

  // 1b. Live baseline — lang / skip-link / main landmark / focus-visible on
  //     every page, scanned or not (conservative: only where the site lacks them).
  startLiveBaseline();

  // 1b. Init analytics (sends widget.boot heartbeat + queues subsequent events).
  //     No-op if browser sends DNT/GPC or localStorage is unavailable.
  initAnalytics({ siteId, apiOrigin });

  // 2. Start watching for text selection — needed so "read selection" works
  //    even after the user clicks the FAB (which clears the live selection).
  startSelectionTracking();

  // 3. Apply pre-computed accessibility fixes from NAGISOT DB.
  //    Runs early, before the FAB even mounts, so fixes appear with the page.
  void applyStoredFixesEarly(opts);

  // 3b. Consent banner (privacy/terms/cookies). Skips silently when the site
  //     owner hasn't enabled it in the dashboard.
  void maybeShowConsentBanner(opts);

  // 4. Mount FAB once DOM is ready (with optional per-site appearance config)
  void mountWithConfig(opts);

  // 5. If the page has legal-document triggers ([data-nagisot-doc] /
  //    [data-nagisot-docs]), lazy-load the accessible document-overlay bundle.
  //    Kept out of loader.js to keep the critical path small.
  maybeLoadDocOverlay(siteId);
})();

function watchCspViolations(origins: string[]): void {
  const seen = new Set<string>();
  document.addEventListener('securitypolicyviolation', (e) => {
    const blocked = e.blockedURI || '';
    const origin = origins.find((o) => blocked.startsWith(o));
    if (!origin) return;
    const directive = (e.effectiveDirective || e.violatedDirective || '').replace(/-(elem|attr)$/, '');
    const key = `${directive} ${origin}`;
    if (seen.has(key)) return;
    seen.add(key);
    console.warn(`[nagisot] Blocked by this site's Content-Security-Policy. Add to the CSP: ${directive} ${origin}`);
  });
}

function maybeLoadDocOverlay(siteId: string): void {
  const has = (): boolean => !!document.querySelector('[data-nagisot-doc],[data-nagisot-docs]');
  const inject = (): void => {
    if (!has()) return;
    if (document.getElementById('nagisot-doc-overlay-js')) return;
    const s = document.createElement('script');
    s.id = 'nagisot-doc-overlay-js';
    s.src = `${WIDGET_CDN_ORIGIN}/doc-overlay.js?${WIDGET_VERSION}`;
    s.defer = true;
    s.dataset.site = siteId;
    document.head.appendChild(s);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', inject, { once: true });
  } else {
    inject();
  }
}

async function mountWithConfig(opts: LoaderOptions): Promise<void> {
  let cfg: WidgetConfig = {};
  try {
    const r = await fetch(`${opts.apiOrigin}/public/widget-config/${encodeURIComponent(opts.siteId)}`, { cache: 'no-store' });
    if (r.ok) cfg = await r.json() as WidgetConfig;
  } catch { /* fall back to defaults */ }
  // Site disabled via Partner API — don't mount anything.
  if ((cfg as { disabled?: boolean }).disabled === true) return;
  // Per-site override CSS (self-test "fix" output) — apply before the FAB mounts.
  if (cfg.featureOverrides) setFeatureOverrides(cfg.featureOverrides);
  // Embed-level corner (data-position) wins over the dashboard default.
  if (opts.position) cfg.position = opts.position;
  const onReady = (): void => {
    // Only allow hiding the FAB when the owner supplied their own trigger element,
    // so the widget is never left with no way to open.
    const trigger = (cfg.triggerSelector || '').trim();
    if (!(cfg.hideFab && trigger)) mountFab(opts, cfg);
    if (trigger) bindCustomTrigger(opts, cfg, trigger);
    // Free-tier page banner — once per session, auto-dismisses.
    // Disclosed prominently at signup; never a surprise to the site owner.
    const showBanner = cfg.showsPageBanner ?? cfg.showsBanner ?? (cfg.tier === 'free');
    if (showBanner) mountFreeTierBanner(cfg);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    onReady();
  }
}

interface WidgetConfig {
  fabBg?: string;
  fabIcon?: string;
  fabBorder?: string;
  accentColor?: string;
  position?: 'br' | 'bl' | 'tr' | 'tl';
  icon?: string;          // preset name OR raw <svg>… OR emoji
  /** Absolute URL of an uploaded raster image (PNG/JPG/WebP) — overrides `icon`. */
  iconImageUrl?: string;
  /** How the uploaded image fills the FAB: 'contain' (icon inside circle, default)
   *  or 'cover' (fills the whole button edge-to-edge — total control). */
  iconImageFit?: 'contain' | 'cover';
  /** CSS selector of the site's OWN element(s) that should open the panel on
   *  click ("bring your own button"). Bound via event delegation. */
  triggerSelector?: string;
  /** Hide the default floating NAGISOT button. Only honored when triggerSelector
   *  is set — otherwise the FAB always mounts so the widget stays openable. */
  hideFab?: boolean;
  /** FAB size: sm 48 · md 58 (default) · lg 68 px */
  fabSize?: 'sm' | 'md' | 'lg';
  /** FAB shape: round (circle, default) or squircle (soft-rounded square) */
  fabShape?: 'round' | 'squircle';
  /** Optional text shown beside the FAB icon (turns it into a pill). */
  fabLabel?: string;
  /** FAB shadow intensity: soft (default) · strong · none */
  fabShadow?: 'soft' | 'strong' | 'none';
  panelTheme?: 'auto' | 'light' | 'dark';
  /** How the panel fits its content. 'auto' (default) shrinks density until
   *  everything is visible without scrolling; 'comfortable' keeps the roomy
   *  look; 'compact' always uses the dense layout. */
  panelLayout?: 'auto' | 'comfortable' | 'compact';
  buttons?: Array<{ key: string; visible: boolean; order: number }>;
  /** Resolved pricing tier (server-decided). Drives free-tier ads. */
  tier?: 'free' | 'starter' | 'pro' | 'business' | 'enterprise';
  /** Small CTA inside the accessibility panel (Free tier). */
  showsPanelCta?: boolean;
  /** Occasional 50px banner at the bottom of the page (Free tier). */
  showsPageBanner?: boolean;
  /** @deprecated — old name for showsPageBanner. Kept for back-compat. */
  showsBanner?: boolean;
  /** Per-site override CSS produced by the self-test "fix" flow. */
  featureOverrides?: string;
}

type StoredFix = {
  ruleId: string; selector: string; xpath?: string | null; contentHash?: string | null;
  fixType: string; payload: { value?: string; attrs?: Record<string, string> } | unknown;
};

// ── Finding a fix's element after the site changed ───────────────────────────
// A selector breaks on any redesign (a class renamed, a block moved). Each stored
// fix also carries an xpath and a content fingerprint taken at scan time, so we
// resolve selector → xpath → fingerprint search, and only ever accept an element
// whose fingerprint still matches: a wrong alt on the wrong image is worse than
// no fix. (Fingerprint = @nagisot/shared/element-id, same code as the scanners.)
const DOC_LEVEL = new Set(['skip-link', 'role-main', 'abbr-expansions', 'jargon-tooltips', 'heading-outline', 'landmark']);
const MAX_CANDIDATES = 1500;
const resolved = new Map<StoredFix, Element>();

function matchesFingerprint(el: Element, want: string): boolean {
  try { return contentHashCandidates(el).includes(want); } catch { return false; }
}

function byXpath(xp: string): Element | null {
  try {
    const n = document.evaluate(xp, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue;
    return n instanceof Element ? n : null;
  } catch { return null; }
}

function tagOfSelector(sel: string): string | null {
  const last = sel.split('>').pop()?.trim() || '';
  const m = /^([a-z][a-z0-9-]*)/i.exec(last);
  return m ? m[1]!.toLowerCase() : null;
}

function resolveFixTarget(fix: StoredFix, deep: boolean): Element | null {
  const prev = resolved.get(fix);
  if (prev?.isConnected) return prev;
  const want = fix.contentHash && fix.contentHash !== 'document' ? fix.contentHash : null;
  let bySel: Element | null = null;
  try { bySel = document.querySelector(fix.selector); } catch { /* invalid selector */ }
  let found: Element | null = null;
  if (!want) {
    found = bySel; // legacy fix without a fingerprint: selector only
  } else if (bySel && matchesFingerprint(bySel, want)) {
    found = bySel;
  } else {
    const x = fix.xpath ? byXpath(fix.xpath) : null;
    if (x && matchesFingerprint(x, want)) {
      found = x;
    } else if (deep) {
      // The element moved: look for its fingerprint among elements of the same tag.
      // (Skipped while the HTML is still streaming — too costly per mutation.)
      const tag = tagOfSelector(fix.selector) || bySel?.tagName.toLowerCase() || x?.tagName.toLowerCase();
      if (tag) {
        const all = document.getElementsByTagName(tag);
        for (let i = 0; i < all.length && i < MAX_CANDIDATES; i++) {
          if (matchesFingerprint(all[i]!, want)) { found = all[i]!; break; }
        }
      }
    }
  }
  if (found) resolved.set(fix, found);
  return found;
}

// ── Page memory ───────────────────────────────────────────────────────────────
// The last fixes served for each page are kept in localStorage so a returning
// visitor gets them synchronously at boot — before first paint and before the
// accessibility tree is built — instead of after a network round trip that only
// started at DOMContentLoaded. The network copy always revalidates and wins.
// (Constants live above bootstrap() — it runs synchronously at module top.)

function fixCacheKey(siteId: string, url: string): string {
  return `${FIX_CACHE_PREFIX}${siteId}|${url}`;
}

function readFixCache(key: string): StoredFix[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw) as { t: number; f: StoredFix[] };
    if (!v || !Array.isArray(v.f) || Date.now() - v.t > FIX_CACHE_TTL_MS) return null;
    return v.f;
  } catch { return null; }
}

function writeFixCache(key: string, fixes: StoredFix[]): void {
  try {
    const index = (JSON.parse(localStorage.getItem(FIX_CACHE_INDEX) || '[]') as string[]).filter((k) => k !== key);
    index.push(key);
    while (index.length > FIX_CACHE_MAX_PAGES) {
      const old = index.shift();
      if (old) localStorage.removeItem(old);
    }
    if (fixes.length) localStorage.setItem(key, JSON.stringify({ t: Date.now(), f: fixes }));
    else localStorage.removeItem(key);
    localStorage.setItem(FIX_CACHE_INDEX, JSON.stringify(index));
  } catch { /* quota / disabled storage — the network path still works */ }
}

async function applyStoredFixesEarly(opts: LoaderOptions): Promise<void> {
  const pageUrl = canonicalUrl();
  const cacheKey = fixCacheKey(opts.siteId, pageUrl);
  // Start the network request NOW — not after DOMContentLoaded.
  const url = `${opts.apiOrigin}/fixes/by-page?siteId=${encodeURIComponent(opts.siteId)}&url=${encodeURIComponent(pageUrl)}`;
  const network = fetch(url, { credentials: 'omit' })
    .then((res) => (res.ok ? res.json() : null))
    .catch(() => null) as Promise<StoredFix[] | null>;

  const cached = readFixCache(cacheKey);
  if (cached?.length) {
    applyFixesWhileParsing(cached, opts);
    try { performance.mark('nagisot:fixes-applied-cache'); } catch { /* noop */ }
  }

  const fixes = await network;
  if (!Array.isArray(fixes)) return;
  writeFixCache(cacheKey, fixes);
  if (document.readyState === 'loading') {
    await new Promise<void>((r) => document.addEventListener('DOMContentLoaded', () => r(), { once: true }));
  }
  scheduleFixPasses(fixes, opts);
  try { performance.mark('nagisot:fixes-applied'); } catch { /* noop */ }
}

/** While the HTML is still streaming in, apply each cached fix the moment its
 *  element is parsed (MutationObserver callbacks run before the next paint). */
function applyFixesWhileParsing(fixes: StoredFix[], opts: LoaderOptions): void {
  let pending = fixes.filter((f) => !applyStoredFix(f, opts, false));
  if (!pending.length || document.readyState !== 'loading' || typeof MutationObserver === 'undefined') {
    scheduleFixPasses(fixes, opts);
    return;
  }
  const mo = new MutationObserver(() => {
    pending = pending.filter((f) => !applyStoredFix(f, opts, false));
    if (!pending.length) mo.disconnect();
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('DOMContentLoaded', () => { mo.disconnect(); scheduleFixPasses(fixes, opts); }, { once: true });
}

// All handlers are idempotent (guard on existing markers/attributes), so we run
// the loop several times: once now, again on window `load`, and a couple of
// delayed passes — this catches content injected by SPA hydration (React/Next/
// Vue) that isn't in the DOM at DOMContentLoaded.
function scheduleFixPasses(fixes: StoredFix[], opts: LoaderOptions): void {
  const applyAll = (): void => { for (const fix of fixes) applyStoredFix(fix, opts); };
  applyAll();
  if (document.readyState !== 'complete') window.addEventListener('load', applyAll, { once: true });
  setTimeout(applyAll, 1200);
  setTimeout(applyAll, 3500);
}

/** Apply one stored fix. Returns true when its target element was found. */
function applyStoredFix(fix: StoredFix, opts: LoaderOptions, deep = true): boolean {
      try {
        if (DOC_LEVEL.has(fix.fixType) && !document.body) return false;
        // Outline and landmarks are judged over the whole page — not a half-parsed one.
        if ((fix.fixType === 'heading-outline' || fix.fixType === 'landmark') && document.readyState === 'loading') return false;
        // Page-level fixes (skip link, main landmark, glossary) aren't tied to one element.
        const el = DOC_LEVEL.has(fix.fixType) || fix.selector === 'document'
          ? document.documentElement
          : resolveFixTarget(fix, deep);
        if (!el) return false;
        const payload = fix.payload as { value?: string; attrs?: Record<string, string> } | undefined;
        if (fix.fixType === 'contrast') {
          // 1.4.3 — measured and corrected live (runtime/contrast-fix.ts).
          if (!applyContrastFix(el, Number(payload?.value) || 4.5)) return false;
        } else if (fix.fixType === 'heading-outline') {
          normalizeHeadingOutline();
        } else if (fix.fixType === 'landmark' && payload?.value) {
          ensureLandmark(payload.value);
        } else if (fix.fixType === 'clickable') {
          makeClickableAccessible(el);
        } else if (fix.fixType === 'media-pause') {
          addMediaPause(el);
        } else if (fix.fixType === 'alt'&& el instanceof HTMLImageElement && payload?.value !== undefined) {
          el.setAttribute('alt', payload.value);
        } else if ((fix.fixType === 'aria-label' || fix.fixType === 'link-text') && payload?.value) {
          el.setAttribute('aria-label', payload.value);
        } else if (fix.fixType === 'autocomplete' && payload?.value) {
          el.setAttribute('autocomplete', payload.value);
        } else if (fix.fixType === 'role' && payload?.value) {
          el.setAttribute('role', payload.value);
        } else if (fix.fixType === 'lang' && payload?.value) {
          el.setAttribute('lang', payload.value);
        } else if (fix.fixType === 'attrs' && payload?.attrs) {
          for (const [k, v] of Object.entries(payload.attrs)) el.setAttribute(k, v);
        } else if (fix.fixType === 'skip-link') {
          ensureSkipLink();
        } else if (fix.fixType === 'role-main') {
          ensureMainLandmark();
        } else if (fix.fixType === 'abbr-expansions') {
          // 3.1.4 — wrap the first occurrence of each abbreviation in <abbr title="…">
          const map = (() => { try { return JSON.parse((payload as { value?: string })?.value || '{}'); } catch { return {}; } })() as Record<string, string>;
          applyAbbrExpansions(map);
        } else if (fix.fixType === 'jargon-tooltips') {
          // 3.1.3 (unusual-words) — same shape as abbr: {term: plain-language definition}
          // → wrap each term in <abbr title="…"> so its meaning is available on hover/AT.
          const map = (() => { try { return JSON.parse((payload as { value?: string })?.value || '{}'); } catch { return {}; } })() as Record<string, string>;
          applyAbbrExpansions(map);
        } else if (fix.fixType === 'text-content' && payload?.value) {
          // headings-descriptive / error-suggestion / sensory-characteristics —
          // the AI produced clearer text. Applied as the ACCESSIBLE NAME (aria-label),
          // never by rewriting the visible DOM: screen-reader users get the improved
          // wording, the visual page is left exactly as the owner authored it.
          el.setAttribute('aria-label', payload.value);
        } else if (fix.fixType === 'media-alternative' && (el instanceof HTMLVideoElement || el instanceof HTMLAudioElement)) {
          // 1.2.5 — surface a "תמליל מלא" link below the video
          const cap = (() => { try { return JSON.parse((payload as { value?: string })?.value || JSON.stringify(payload || {})); } catch { return {}; } })() as { url?: string; partial?: boolean };
          if (cap.url && !el.parentElement?.querySelector('a[data-nagisot-transcript]')) {
            const link = document.createElement('a');
            link.href = cap.url.startsWith('http') ? cap.url : `${opts.apiOrigin}${cap.url}`;
            link.target = '_blank';
            link.rel = 'noopener';
            link.dataset.nagisotTranscript = '1';
            link.textContent = cap.partial ? 'תמליל מלא (בעיבוד)' : 'תמליל מלא עם תיאור חזותי';
            link.style.cssText = 'display:inline-block;margin-top:8px;padding:6px 12px;background:#0B1220;color:#B8956A;text-decoration:none;border-radius:2px;font:600 12px/1 system-ui,sans-serif;letter-spacing:.04em';
            el.parentElement?.insertBefore(link, el.nextSibling);
          }
        } else if (fix.fixType === 'captions' && (el instanceof HTMLVideoElement || el instanceof HTMLAudioElement)) {
          // Whisper-generated captions: payload.value is JSON {vttUrl, lang}
          const cap = (() => { try { return JSON.parse((payload as { value?: string })?.value || '{}'); } catch { return {}; } })() as { vttUrl?: string; lang?: string };
          if (cap.vttUrl) {
            const existing = el.querySelector('track[kind="captions"], track[kind="subtitles"]');
            if (!existing) {
              const track = document.createElement('track');
              track.kind = 'captions';
              track.label = cap.lang === 'he' ? 'עברית' : (cap.lang || 'auto').toUpperCase();
              track.srclang = cap.lang || 'auto';
              track.src = cap.vttUrl.startsWith('http') ? cap.vttUrl : `${opts.apiOrigin}${cap.vttUrl}`;
              track.default = true;
              el.appendChild(track);
            }
          }
        }
        el.setAttribute('data-nagisot-fix', fix.ruleId);
        return true;
      } catch { return true; /* bad selector — never retry it */ }
}

/**
 * 3.1.4 — wrap the first occurrence of each abbreviation in an <abbr title>.
 * Walks visible text nodes only (skips script/style/existing abbr), touches
 * each abbreviation once, and never runs inside the widget's own shadow root.
 */
function applyAbbrExpansions(map: Record<string, string>): void {
  const entries = Object.entries(map).filter(([k, v]) => k && v && k.length >= 2 && k.length <= 10);
  if (!entries.length) return;
  const remaining = new Map(entries.map(([k, v]) => [k, v] as const));
  const SKIP = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'ABBR', 'CODE', 'PRE', 'TEXTAREA']);
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const p = node.parentElement;
      if (!p || SKIP.has(p.tagName)) return NodeFilter.FILTER_REJECT;
      if (p.closest('[data-nagisot-abbr],.nagisot-skip')) return NodeFilter.FILTER_REJECT;
      return node.nodeValue && node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });
  const targets: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) targets.push(n as Text);
  for (const textNode of targets) {
    if (!remaining.size) break;
    const text = textNode.nodeValue || '';
    for (const [abbr, title] of remaining) {
      const idx = text.indexOf(abbr);
      if (idx < 0) continue;
      // Word-boundary-ish guard: neighbours must not be alphanumerics of the same script.
      const before = text[idx - 1] || ' ';
      const after = text[idx + abbr.length] || ' ';
      if (/[A-Za-z0-9]/.test(before) || /[A-Za-z0-9]/.test(after)) continue;
      const range = document.createRange();
      range.setStart(textNode, idx);
      range.setEnd(textNode, idx + abbr.length);
      const el = document.createElement('abbr');
      el.title = title;
      el.setAttribute('data-nagisot-abbr', '1');
      try { range.surroundContents(el); } catch { continue; }
      remaining.delete(abbr);
      break; // one abbreviation per text node per pass keeps ranges valid
    }
  }
}

function canonicalUrl(): string {
  try {
    const u = new URL(location.href);
    u.hash = '';
    return u.origin + u.pathname.replace(/\/$/, '') || '/';
  } catch {
    return location.href;
  }
}

function applySavedPrefs(): void {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return;
    const prefs = JSON.parse(raw) as UserPrefs;
    applyClientPrefs(prefs);
  } catch {
    // ignore
  }
}

const ICON_PRESETS: Record<string, string> = {
  human: ICON_SVG,
  eye: `<svg viewBox="0 0 24 24" width="22" height="22"><path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3.2" fill="currentColor"/></svg>`,
  wheelchair: `<svg viewBox="0 0 24 24" width="22" height="22"><circle cx="9.5" cy="4.2" r="1.6" fill="currentColor"/><path fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" d="M9 7v6h6l3 6"/><circle cx="11" cy="17" r="4.5" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>`,
  ear: `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6.6 21c-2 0-3.4-1.7-3.4-3.6 0-3 1.6-5 1.6-8.4C4.8 5 7.7 2 12 2s7 3 7 6.4c0 4-3 4.8-3 7.8C16 19 14.3 21 12 21"/><path d="M9 9c0-1.7 1.3-3 3-3s3 1.3 3 3-1.3 2.5-3 3-3 1.5-3 3"/></svg>`,
  plus: `<svg viewBox="0 0 24 24" width="22" height="22"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M12 7v10M7 12h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  star: `<svg viewBox="0 0 24 24" width="22" height="22"><path fill="currentColor" d="M12 2l2.7 6.4 6.9.6-5.2 4.6 1.6 6.8L12 16.9l-6 3.5 1.6-6.8L2.4 9l6.9-.6L12 2z"/></svg>`,
};

function pickIcon(name: string | undefined): string {
  if (!name) return ICON_PRESETS.human!;
  if (name in ICON_PRESETS) return ICON_PRESETS[name]!;
  // Custom: raw SVG markup
  if (name.trim().startsWith('<svg')) return name;
  // Emoji or single char
  if (name.length <= 4) return `<span style="font-size:22px;line-height:1;">${name}</span>`;
  return ICON_PRESETS.human!;
}

// Physical (visual) corner placement — NOT logical — so the corner the owner
// picks in the dashboard is the corner that actually appears, regardless of the
// host page's direction (on RTL sites, logical inset-inline-end flips to the
// left and lands the FAB on top of the site's own bottom-left widgets).
// `inset:auto` first resets the base FAB_CSS insets so only the chosen edges win.
function positionCss(pos: string | undefined): string {
  switch (pos) {
    case 'bl': return 'inset:auto; bottom:22px; left:22px;';
    case 'tr': return 'inset:auto; top:22px; right:22px;';
    case 'tl': return 'inset:auto; top:22px; left:22px;';
    case 'br':
    default:   return 'inset:auto; bottom:22px; right:22px;';
  }
}

// ── Draggable FAB position (persisted per-visitor) ──────────────────────────
// The site owner sets a DEFAULT corner (cfg.position). A visitor can drag the
// FAB anywhere; that choice is saved in localStorage and restored next visit.
//
// IMPORTANT: positions are anchored with right/bottom (distance from the bottom
// -right corner), NOT left/top. Some host pages (smooth-scroll wrappers, certain
// builders) break `position:fixed; top/left` so the element renders far off the
// viewport — which previously made a dragged FAB vanish and then persist that
// invisible spot. right/bottom is the same model the resting FAB already uses
// successfully, so it renders correctly everywhere. Values are clamped to the
// viewport on apply, save, load and resize, so the FAB can never end up hidden.
const FAB_POS_KEY = 'nagisot:fab-pos:v2'; // v2: right/bottom model (old v1 ignored)
const FAB_DRAG_THRESHOLD = 6; // px before a press becomes a drag (vs a click)

interface FabPos { siteId: string; right: number; bottom: number; }

function loadFabPos(siteId: string): FabPos | null {
  try {
    const raw = localStorage.getItem(FAB_POS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as FabPos;
    if (p && p.siteId === siteId && Number.isFinite(p.right) && Number.isFinite(p.bottom)) return p;
  } catch { /* unavailable */ }
  return null;
}
function saveFabPos(pos: FabPos): void {
  try { localStorage.setItem(FAB_POS_KEY, JSON.stringify(pos)); } catch { /* DNT/incognito */ }
}
function clearFabPos(): void {
  try { localStorage.removeItem(FAB_POS_KEY); } catch { /* noop */ }
}
/** Clamp right/bottom offsets so the FAB always stays fully on-screen. */
function clampRB(right: number, bottom: number, w: number, h: number): { right: number; bottom: number } {
  const m = 8; // keep at least 8px from each edge
  const maxRight = Math.max(m, window.innerWidth - w - m);
  const maxBottom = Math.max(m, window.innerHeight - h - m);
  return {
    right: Math.min(Math.max(m, right), maxRight),
    bottom: Math.min(Math.max(m, bottom), maxBottom),
  };
}
function applyFabFixedPos(btn: HTMLElement, right: number, bottom: number): void {
  btn.style.position = 'fixed';
  // Reset every inset (physical + logical) FIRST, then set right/bottom.
  // Order matters: setting a logical inset (inset-inline-end / inset-block-end)
  // AFTER right/bottom would cancel them (they map to the same edges), leaving
  // the FAB with all-auto insets — it then falls to its static flow position at
  // the very bottom of the document and renders off-screen.
  btn.style.inset = 'auto';
  btn.style.insetInlineStart = 'auto';
  btn.style.insetInlineEnd = 'auto';
  btn.style.insetBlockStart = 'auto';
  btn.style.insetBlockEnd = 'auto';
  btn.style.left = 'auto';
  btn.style.top = 'auto';
  btn.style.right = `${right}px`;
  btn.style.bottom = `${bottom}px`;
}
function clearFabInlinePos(btn: HTMLElement): void {
  // Revert to the owner default (CSS inset from positionCss reapplies).
  for (const p of ['position', 'inset', 'left', 'top', 'right', 'bottom',
    'inset-block-start', 'inset-block-end', 'inset-inline-start', 'inset-inline-end']) {
    btn.style.removeProperty(p);
  }
}

/** Wire pointer-drag + persistence onto the FAB. Returns `wasDragged()` so the
 *  click handler can swallow the click that ends a real drag. */
function makeFabDraggable(btn: HTMLElement, siteId: string): () => boolean {
  // Restore a saved position (clamped) on mount.
  const saved = loadFabPos(siteId);
  if (saved) {
    const c = clampRB(saved.right, saved.bottom, 58, 58);
    applyFabFixedPos(btn, c.right, c.bottom);
  }

  let down: { x: number; y: number; grabX: number; grabY: number; w: number; h: number } | null = null;
  let moved = false;
  let last: { right: number; bottom: number } | null = null;

  btn.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    // Measure at rest (right/bottom model renders correctly, so rect is valid).
    const r = btn.getBoundingClientRect();
    down = { x: e.clientX, y: e.clientY, grabX: e.clientX - r.left, grabY: e.clientY - r.top, w: r.width, h: r.height };
    moved = false;
    try { btn.setPointerCapture(e.pointerId); } catch { /* noop */ }
  });
  btn.addEventListener('pointermove', (e) => {
    if (!down) return;
    if (!moved && Math.hypot(e.clientX - down.x, e.clientY - down.y) < FAB_DRAG_THRESHOLD) return;
    moved = true;
    btn.classList.add('nag-dragging');
    // Desired top-left in viewport coords → convert to right/bottom offsets.
    const left = e.clientX - down.grabX;
    const top = e.clientY - down.grabY;
    last = clampRB(window.innerWidth - (left + down.w), window.innerHeight - (top + down.h), down.w, down.h);
    applyFabFixedPos(btn, last.right, last.bottom);
  });
  const end = (e: PointerEvent): void => {
    if (!down) return;
    try { btn.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    if (moved && last) {
      btn.classList.remove('nag-dragging');
      saveFabPos({ siteId, right: last.right, bottom: last.bottom }); // save computed (clamped) values, not getBoundingClientRect
      track('fab.moved');
    }
    down = null;
  };
  btn.addEventListener('pointerup', end);
  btn.addEventListener('pointercancel', end);

  // Keep on-screen across viewport changes.
  window.addEventListener('resize', () => {
    const cur = loadFabPos(siteId);
    if (!cur) return;
    const c = clampRB(cur.right, cur.bottom, 58, 58);
    applyFabFixedPos(btn, c.right, c.bottom);
    saveFabPos({ siteId, right: c.right, bottom: c.bottom });
  });

  // Reset request from the panel footer (CustomEvent across shadow roots).
  window.addEventListener('nagisot:reset-fab-pos', () => {
    clearFabPos();
    clearFabInlinePos(btn);
  });

  return () => moved;
}

/** Per-site FAB appearance overrides (size, shape, shadow, optional label). */
function fabCustomCss(cfg: WidgetConfig): string {
  const size = cfg.fabSize === 'sm' ? 48 : cfg.fabSize === 'lg' ? 68 : 58;
  const hasLabel = !!(cfg.fabLabel && cfg.fabLabel.trim());
  const radius = hasLabel ? '999px' : cfg.fabShape === 'squircle' ? `${Math.round(size * 0.3)}px` : '50%';
  const shadow =
    cfg.fabShadow === 'none'
      ? 'none'
      : cfg.fabShadow === 'strong'
        ? '0 16px 38px rgba(11,18,32,.42), inset 0 1px 0 rgba(255,255,255,.08)'
        : '0 8px 24px rgba(11,18,32,.22), 0 0 0 0 rgba(184,149,106,0), inset 0 1px 0 rgba(255,255,255,.06)';
  const iconPx = Math.round(size * 0.38);
  return `
    .fab {
      ${positionCss(cfg.position)}
      ${hasLabel
        ? `width:auto; min-width:${size}px; padding:0 ${Math.round(size * 0.34)}px; gap:10px; border-radius:999px;`
        : `width:${size}px; border-radius:${radius};`}
      height:${size}px;
      background: ${cfg.fabBg || 'radial-gradient(circle at 30% 30%, #1a2433 0%, #0B1220 60%, #060a13 100%)'};
      color: ${cfg.fabIcon || '#D7B98C'};
      border-color: ${cfg.fabBorder || 'rgba(184,149,106,0.45)'};
      box-shadow: ${shadow};
    }
    .fab svg { width:${iconPx}px; height:${iconPx}px; }
    .fab .fab-label {
      font: 600 ${Math.max(13, Math.round(size * 0.26))}px/1 'Assistant', -apple-system, 'Segoe UI', system-ui, sans-serif;
      white-space: nowrap; letter-spacing: .01em;
    }
  `;
}

function mountFab(opts: LoaderOptions, cfg: WidgetConfig = {}): void {
  if (document.getElementById(HOST_ID)) return;

  const host = document.createElement('div');
  host.id = HOST_ID;
  host.setAttribute('data-nagisot', SCRIPT_TAG_NAME);
  document.body.appendChild(host);

  const shadow = host.attachShadow({ mode: 'open' });
  panelShadowRef = shadow; // reused by a custom trigger so the panel shares one root

  const style = document.createElement('style');
  style.textContent = FAB_CSS + fabCustomCss(cfg);
  shadow.appendChild(style);

  const label = (cfg.fabLabel || '').trim();
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = label ? 'fab fab-has-label' : 'fab';
  btn.setAttribute('aria-label', label || 'פתח תפריט נגישות');
  btn.setAttribute('aria-haspopup', 'dialog');
  // Custom uploaded image takes precedence over preset/svg/emoji icon.
  const imgUrl = (cfg.iconImageUrl || '').trim();
  const useImg = /^https?:\/\//i.test(imgUrl);
  // 'cover' = the image fills the entire button edge-to-edge (total control);
  // 'contain' (default) = the image sits as an icon inside the colored circle.
  const fullBleed = useImg && cfg.iconImageFit === 'cover' && !label;
  const imgStyle = fullBleed
    ? 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border-radius:inherit;display:block'
    : 'width:62%;height:62%;object-fit:contain;display:block';
  const iconHtml = useImg
    ? `<img class="fab-img" src="${escapeHtml(imgUrl)}" alt="" decoding="async" style="${imgStyle}">`
    : pickIcon(cfg.icon);
  if (fullBleed) { btn.style.position = 'relative'; btn.style.overflow = 'hidden'; btn.style.padding = '0'; }
  btn.innerHTML = iconHtml + (label ? `<span class="fab-label">${escapeHtml(label)}</span>` : '');
  if (useImg) {
    const img = btn.querySelector('img.fab-img');
    // If the custom image fails to load, fall back to the preset/svg/emoji icon.
    img?.addEventListener('error', () => {
      img.remove();
      if (fullBleed) { btn.style.overflow = ''; btn.style.padding = ''; }
      btn.insertAdjacentHTML('afterbegin', pickIcon(cfg.icon));
    });
  }
  shadow.appendChild(btn);

  // Prevent the click from stealing focus and clearing the page text selection
  // — critical for the "read selection" feature inside the panel.
  btn.addEventListener('mousedown', (e) => e.preventDefault());

  // Draggable + per-visitor persisted position. wasDragged() lets us swallow
  // the synthetic click that fires at the end of a real drag.
  const wasDragged = makeFabDraggable(btn, opts.siteId);

  btn.addEventListener('click', async (e) => {
    if (wasDragged()) { e.preventDefault(); e.stopImmediatePropagation(); return; }
    btn.setAttribute('aria-busy', 'true');
    try { await openWidgetPanel(opts, cfg, shadow); }
    finally { btn.removeAttribute('aria-busy'); }
  });
}

// ── Shared panel-open flow (used by the FAB and any custom trigger) ─────────
let panelShadowRef: ShadowRoot | null = null;
let panelOpening = false;

/** Lazily create a dedicated shadow root for the panel when no FAB is mounted. */
function ensurePanelShadow(): ShadowRoot {
  if (panelShadowRef) return panelShadowRef;
  const host = document.createElement('div');
  host.id = 'nagisot-panel-host';
  host.setAttribute('data-nagisot', SCRIPT_TAG_NAME);
  document.body.appendChild(host);
  panelShadowRef = host.attachShadow({ mode: 'open' });
  return panelShadowRef;
}

async function openWidgetPanel(opts: LoaderOptions, cfg: WidgetConfig, shadow: ShadowRoot): Promise<void> {
  if (panelOpening) return;
  panelOpening = true;
  track('panel.open');
  try {
    const mod = await import(/* @vite-ignore */ opts.widgetUrl);
    await mod.openPanel({ shadow, siteId: opts.siteId, apiOrigin: opts.apiOrigin, widgetConfig: cfg });
  } finally {
    panelOpening = false;
  }
}

/**
 * "Bring your own button" — bind the owner's chosen element(s) to open the panel.
 * Uses event delegation so dynamically-rendered elements work too, and adds
 * basic dialog-trigger semantics for keyboard + screen-reader users.
 */
function bindCustomTrigger(opts: LoaderOptions, cfg: WidgetConfig, selector: string): void {
  const enhance = (): void => {
    let matched: Element[] = [];
    try { matched = Array.from(document.querySelectorAll(selector)); } catch { return; }
    for (const el of matched) {
      if ((el as HTMLElement).dataset?.nagTrigger === '1') continue;
      (el as HTMLElement).dataset.nagTrigger = '1';
      el.setAttribute('aria-haspopup', 'dialog');
      const tag = el.tagName;
      if (tag !== 'BUTTON' && tag !== 'A') {
        if (!el.hasAttribute('role')) el.setAttribute('role', 'button');
        if ((el as HTMLElement).tabIndex < 0) (el as HTMLElement).tabIndex = 0;
      }
    }
  };
  enhance();

  const open = (e: Event): void => {
    e.preventDefault();
    void openWidgetPanel(opts, cfg, ensurePanelShadow());
  };
  // Delegated so it survives re-renders and late-mounted elements.
  document.addEventListener('click', (e) => {
    const t = (e.target as Element)?.closest?.(selector);
    if (t) open(e);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const t = (e.target as Element)?.closest?.(selector);
    if (t && t.tagName !== 'BUTTON' && t.tagName !== 'A') open(e);
  });
  // Re-tag late-rendered matches so they get the a11y attributes too.
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(enhance).observe(document.body, { childList: true, subtree: true });
  }
}

// ── Free-tier page banner ──────────────────────────────────────────────────
//
// Small 50px-tall bar at the bottom of the page on Free-tier customer sites.
// Idan's spec: "פרסומת אחת מידי פעם לדף" — occasional, not in-your-face.
//
// Behavior:
//   - Shown ONCE per visitor session (sessionStorage flag)
//   - Auto-dismisses after 12 seconds with a graceful fade-out
//   - Manual close button works at any time
//   - Skipped entirely on viewports < 360px (mobile UX)
//   - Respects prefers-reduced-motion (no animation, just instant show)
//   - Lives in its own Shadow DOM so customer CSS can't break it OR be broken
//   - Disclosed to the site owner at signup as a required checkbox
//   - Removed entirely by upgrading to Starter (₪30+VAT/mo) or higher

const BANNER_HOST_ID = 'nagisot-promo-host';
const BANNER_SHOWN_KEY = 'nagisot.promo.shown';
const BANNER_AUTODISMISS_MS = 12_000;

function mountFreeTierBanner(cfg: WidgetConfig): void {
  if (document.getElementById(BANNER_HOST_ID)) return;
  // Once per session: if we already showed (and auto-dismissed) the banner
  // this session, don't show again.
  try { if (sessionStorage.getItem(BANNER_SHOWN_KEY) === '1') return; } catch { /* incognito */ }
  if (window.innerWidth < 360) return;
  try { sessionStorage.setItem(BANNER_SHOWN_KEY, '1'); } catch { /* noop */ }

  track('widget.banner_shown');

  const host = document.createElement('div');
  host.id = BANNER_HOST_ID;
  document.documentElement.appendChild(host);
  const sh = host.attachShadow({ mode: 'open' });

  const accent = cfg.accentColor || '#B8956A';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  sh.innerHTML = `
    <style>
      :host { all: initial; }
      .bar {
        position: fixed; left: 0; right: 0; bottom: 0; z-index: 2147483644;
        background: rgba(11,18,32,0.92); color: #F4F1EB;
        backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
        border-top: 1px solid rgba(255,255,255,0.08);
        font: 12px/1.45 'Assistant', -apple-system, system-ui, 'Segoe UI', sans-serif;
        padding: 9px 16px;
        display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
        transition: opacity .55s ease, transform .55s ease;
      }
      .bar.in  { animation: nag-bnr-in .35s cubic-bezier(.16,1,.3,1) both; }
      .bar.out { opacity: 0; transform: translateY(100%); pointer-events: none; }
      @keyframes nag-bnr-in {
        from { opacity: 0; transform: translateY(100%); }
        to   { opacity: 1; transform: none; }
      }
      @media (prefers-reduced-motion: reduce) {
        .bar, .bar.in, .bar.out { animation: none !important; transition: opacity .2s linear !important; transform: none !important; }
      }
      .dot {
        width: 6px; height: 6px; border-radius: 50%; background: ${accent};
        box-shadow: 0 0 0 2px rgba(184,149,106,.18);
        flex-shrink: 0;
      }
      .text { flex: 1 1 auto; min-width: 0; }
      .text a {
        color: ${accent}; text-decoration: none; font-weight: 600;
        border-bottom: 1px solid transparent; transition: border-color .15s;
      }
      .text a:hover, .text a:focus-visible { border-bottom-color: ${accent}; outline: none; }
      .upgrade {
        flex-shrink: 0;
        background: ${accent}; color: #0B1220;
        padding: 6px 14px; border-radius: 2px;
        font: 600 11px 'Assistant', sans-serif; letter-spacing: .05em;
        text-decoration: none; text-transform: uppercase;
        transition: filter .15s, transform .15s;
      }
      .upgrade:hover { filter: brightness(1.08); transform: translateY(-1px); }
      .close {
        flex-shrink: 0;
        background: transparent; border: 0; color: rgba(244,241,235,.6);
        width: 26px; height: 26px; padding: 0; border-radius: 50%;
        cursor: pointer; font: 16px/1 system-ui;
        display: inline-flex; align-items: center; justify-content: center;
        transition: background .15s, color .15s;
      }
      .close:hover { background: rgba(255,255,255,.08); color: #fff; }
      .close:focus-visible { outline: 2px solid ${accent}; outline-offset: 2px; }
      @media (max-width: 540px) {
        .bar { padding: 8px 12px; font-size: 11px; gap: 10px; }
        .text-long { display: none; }
        .upgrade { padding: 5px 10px; font-size: 10px; }
      }
    </style>
    <div class="bar in" role="complementary" aria-label="NAGISOT — שיתוף קצר">
      <span class="dot" aria-hidden="true"></span>
      <span class="text">
        <span class="text-long">תוסף הנגישות מופעל ע״י</span>
        <a href="https://nagis.doogree.co.il?ref=page-banner" target="_blank" rel="noopener">NAGISOT</a>
        <span class="text-long"> — נגישות מלאה לאתר שלך בלי קוד.</span>
      </span>
      <a class="upgrade" href="https://nagis.doogree.co.il/pricing.html?ref=page-banner" target="_blank" rel="noopener">צור גם אתה</a>
      <button class="close" type="button" aria-label="סגור">×</button>
    </div>
  `;

  const bar = sh.querySelector('.bar') as HTMLElement | null;
  const closeBtn = sh.querySelector('.close');
  let dismissed = false;
  const dismiss = (): void => {
    if (dismissed || !bar) return;
    dismissed = true;
    bar.classList.add('out');
    setTimeout(() => host.remove(), reducedMotion ? 220 : 600);
  };
  closeBtn?.addEventListener('click', () => { track('widget.banner_close'); dismiss(); });
  // Track clicks on the "upgrade and remove" button or the NAGISOT brand link
  sh.querySelectorAll('a').forEach((a) => {
    a.addEventListener('click', () => track('widget.banner_cta', { href: (a as HTMLAnchorElement).href }));
  });
  // Auto-fade after 12 seconds — "פרסומת אחת מידי פעם", not an always-on bar
  setTimeout(dismiss, BANNER_AUTODISMISS_MS);
}

// ── Consent banner ─────────────────────────────────────────────────────────
//
// Tiny privacy/terms/cookies notice that anchors to the bottom of the page.
// Visit the site → server says show=true → render banner. User clicks
//   • "אני מסכים"   → server stores accept, expires_at = now + retentionDays
//   • "לא, תודה"    → server stores decline, expires_at = NULL
//                     so the banner re-shows on every subsequent pageview.
// Hidden silently when the site owner hasn't enabled it.

const CONSENT_LS_KEY = 'nagisot.consent.vid';

async function maybeShowConsentBanner(opts: LoaderOptions): Promise<void> {
  try {
    let vid = '';
    try { vid = localStorage.getItem(CONSENT_LS_KEY) || ''; } catch { /* incognito etc. */ }
    if (!vid) {
      vid = (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now());
      try { localStorage.setItem(CONSENT_LS_KEY, vid); } catch { /* noop */ }
    }
    const checkUrl = `${opts.apiOrigin}/public/consent/${encodeURIComponent(opts.siteId)}/check?vid=${encodeURIComponent(vid)}`;
    const res = await fetch(checkUrl, { credentials: 'omit', cache: 'no-store' });
    if (!res.ok) return;
    const data = await res.json() as { show?: boolean; config?: ConsentConfig };
    if (!data.show) return;
    renderConsentBanner(opts, vid, data.config || {});
  } catch {
    /* offline / non-fatal — skip */
  }
}

interface ConsentConfig {
  language?: string;
  accentColor?: string;
  position?: 'bottom' | 'top';
  message?: string;
  acceptLabel?: string;
  declineLabel?: string;
  privacyUrl?: string;
  termsUrl?: string;
  retentionDays?: number;
}

function renderConsentBanner(opts: LoaderOptions, vid: string, cfg: ConsentConfig): void {
  if (document.getElementById('__nagisot_consent')) return;
  const host = document.createElement('div');
  host.id = '__nagisot_consent';
  document.documentElement.appendChild(host);
  const sh = host.attachShadow({ mode: 'open' });

  const accent = cfg.accentColor || '#B8956A';
  const lang = cfg.language || document.documentElement.lang || 'he';
  const isRtl = lang === 'he' || lang === 'ar';
  const position = cfg.position === 'top' ? 'top' : 'bottom';
  const msg = cfg.message || (isRtl
    ? 'אנחנו משתמשים בעוגיות לשיפור החוויה ולניתוח השימוש באתר. בלחיצה על "אני מסכים" אתה מאשר את מדיניות הפרטיות והתקנון שלנו.'
    : 'We use cookies to improve your experience and analyze usage. By clicking "Accept", you agree to our Privacy Policy and Terms.');
  const acceptLbl = cfg.acceptLabel || (isRtl ? 'אני מסכים' : 'I agree');
  const declineLbl = cfg.declineLabel || (isRtl ? 'לא, תודה' : 'No thanks');
  const privacyTxt = isRtl ? 'מדיניות פרטיות' : 'Privacy Policy';
  const termsTxt = isRtl ? 'תקנון' : 'Terms';

  sh.innerHTML = `
    <style>
      :host { all: initial; }
      .wrap {
        position: fixed; inset-inline-start: 16px; inset-inline-end: 16px;
        ${position === 'top' ? 'top: 16px;' : 'bottom: 16px;'}
        max-width: 720px; margin: 0 auto;
        background: #FAF8F4; color: #0B1220;
        border: 1px solid rgba(11,18,32,.12); border-inline-start: 4px solid ${accent};
        box-shadow: 0 12px 40px rgba(11,18,32,.16); border-radius: 2px;
        padding: 16px 20px;
        font: 14px/1.55 'Assistant', -apple-system, system-ui, sans-serif;
        z-index: 2147483640; display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
        direction: ${isRtl ? 'rtl' : 'ltr'};
        animation: nag-slide .3s ease;
      }
      @keyframes nag-slide {
        from { opacity: 0; transform: translateY(${position === 'top' ? '-16px' : '16px'}); }
        to   { opacity: 1; transform: none; }
      }
      .text { flex: 1; min-width: 240px; }
      .text strong { font: 600 14px 'Assistant', sans-serif; }
      .links { display: flex; gap: 14px; margin-top: 6px; font-size: 12px; }
      .links a { color: #8F6F4D; text-decoration: underline; text-underline-offset: 3px; }
      .links a:hover { color: ${accent}; }
      .btns { display: flex; gap: 8px; align-items: center; flex-shrink: 0; }
      button {
        font: 600 13px 'Assistant', sans-serif; letter-spacing: .04em;
        padding: 10px 18px; border-radius: 2px; cursor: pointer;
        border: 1px solid transparent;
        transition: background .15s, color .15s;
      }
      .accept { background: #0B1220; color: ${accent}; border-color: #0B1220; }
      .accept:hover { background: #1c2433; color: #DEC397; }
      .decline { background: transparent; color: #6B6356; border-color: rgba(11,18,32,.18); }
      .decline:hover { background: rgba(11,18,32,.05); color: #0B1220; }
      @media (max-width: 540px) {
        .wrap { padding: 14px 16px; }
        .btns { width: 100%; }
        .btns button { flex: 1; }
      }
    </style>
    <div class="wrap" role="dialog" aria-labelledby="nag-consent-title" aria-modal="false">
      <div class="text">
        <strong id="nag-consent-title">${escapeHtml(isRtl ? 'הגנת הפרטיות שלך' : 'Your privacy')}</strong>
        <div>${escapeHtml(msg)}</div>
        <div class="links">
          ${cfg.privacyUrl ? `<a href="${escapeAttr(cfg.privacyUrl)}" target="_blank" rel="noopener">${escapeHtml(privacyTxt)}</a>` : ''}
          ${cfg.termsUrl ? `<a href="${escapeAttr(cfg.termsUrl)}" target="_blank" rel="noopener">${escapeHtml(termsTxt)}</a>` : ''}
        </div>
      </div>
      <div class="btns">
        <button class="decline" type="button" id="nag-decline">${escapeHtml(declineLbl)}</button>
        <button class="accept"  type="button" id="nag-accept">${escapeHtml(acceptLbl)}</button>
      </div>
    </div>
  `;

  function decide(decision: 'accept' | 'decline'): void {
    fetch(`${opts.apiOrigin}/public/consent/${encodeURIComponent(opts.siteId)}/decide`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ vid, decision, url: location.href }),
      credentials: 'omit',
      cache: 'no-store',
    }).catch(() => {});
    host.remove();
  }
  sh.getElementById('nag-accept')?.addEventListener('click', () => decide('accept'));
  sh.getElementById('nag-decline')?.addEventListener('click', () => decide('decline'));
}

function escapeHtml(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
function escapeAttr(s: string): string { return escapeHtml(s); }
